var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    Canvas                      = require("enyo-canvas/Canvas"),
    EventDispatchSupport        = require("enyo-canvas/EventDispatchSupport"),
    Image                       = require("ott/image-viewer/Image"),
    PerformanceModeQueueSupport = require("enyo-perf/PerformanceModeQueueSupport"),
    kind                        = require("enyo/kind");

/**
 * [Viewport]{@link ott.image-viewer.Viewport} displays images rendered on a
 * canvas tag that can be navigated by mouse/touch gestures or through commands.
 *
 * Unlike ott.image-viewer it handles only a single image at a time.
 *
 * @class   ott.image-viewer.Viewport
 * @extends enyo-canvas.Canvas
 * @mixes   enyo-perf.AbsoluteShowingQueueSupport
 * @mixes   enyo-perf.PerformanceModeQueueSupport
 * @mixes   enyo-canvas.EventDispatchSupport
 * @abstract
 * @public
 */
module.exports = kind(/** @lends ott.image-viewer.Viewport.prototype */ {

    /**
     * @private
     */
    name: "ott.image-viewer.Viewport",

    /**
     * @private
     */
    kind: Canvas,

    /**
     * @private
     */
    style: "touch-action: none;",

    /**
     * @private
     */
    mixins: [
        AbsoluteShowingQueueSupport,
        PerformanceModeQueueSupport,
        EventDispatchSupport
    ],

    /**
     * @private
     */
    classes: "ott-image-viewer-viewport",

    /**
     * The kind that will render an image.
     *
     * @type {enyo-canvas.CanvasControl}
     * @public
     */
    imageKind: Image,

    /**
     * Optional extra options to merge into the image kind at creation time.
     *
     * @type {Object=}
     * @public
     */
    imageOptions: null,

    /**
     * This object maps the unique id of the dataset to the CanvasControl that
     * displays the image. This mapping will be updated any time an image is
     * created or destroyed.
     *
     * @type {Object}
     * @private
     */
    uidToImage: null,

    /**
     * The currently active image
     *
     * @type {?enyo-canvas.CanvasControl}
     * @readonly
     * @public
     */
    activeImage: null,

    /**
     * The dataset containing the current image.
     *
     * @type {*}
     * @public
     */
    data: null,

    /**
     * Sets the direction from which the next image should be revealed and the
     * previous should be hidden. Supported values are `forward` and `backward`:
     *  - `forward` lets the next image slide in from the right and lets the
     *    previous image slide out to the left.
     *  - `backward` lets the next image slide in from the left and lets the
     *    previous image slide out to the right.
     *
     * @type    {?string}
     * @default "forward"
     * @public
     */
    direction: "forward",

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.uidToImage = {};
            sup.apply(this, arguments);
        };
    }),

    /**
     * Initial viewport places world-coordinates 0,0 at the middle of the
     * canvas. Pixel coordinates are rounded down to avoid sub-pixel rendering,
     * which is better for drawing performance.
     *
     * @protected
     */
    setupViewport: function() {
        var bounds   = this.boundingClientRect,
            viewport = {
                width:  bounds.width,
                height: bounds.height
            };

        // Avoid non-integer pixels for speed
        viewport.x = Math.round(viewport.width / -2);
        viewport.y = Math.round(viewport.height / -2);
        this.renderer.setTransform(1, 0, 0, 1, -viewport.x, -viewport.y);
        this.set("viewport", viewport);
    },

    /**
     * Resize the canvas children in response too resized viewport.
     *
     * @param  {enyo-canvas.Viewport}  previousBounds  The previous bounds of
     *                                                 the canvas.
     * @param  {enyo-canvas.Viewport}  currentBounds   The current bounds of the
     *                                                 canvas.
     * @private
     */
    viewportChanged: function(previousBounds, currentBounds) {
        var xMultiplier,
            yMultiplier,
            multiplier,
            i,
            n,
            children = this.children;

        if (!previousBounds) {
            return;
        }

        // Calculate multiplier
        if (currentBounds.width !== 0 && previousBounds.width !== 0) {
            xMultiplier = currentBounds.width / previousBounds.width;
        }
        else {
            xMultiplier = 1;
        }

        if (currentBounds.height !== 0 && previousBounds.height !== 0) {
            yMultiplier = currentBounds.height / previousBounds.height;
        }
        else {
            yMultiplier = 1;
        }

        multiplier = xMultiplier < yMultiplier ? xMultiplier : yMultiplier;

        // Resize the children
        for (i = 0, n = children.length; i < n; i++) {
            children[i].reflow(multiplier);
        }
    },

    // Data
    // --------------------------------

    /**
     * React to change in data, hide the active image if it no longer matches
     * the new dataset and reveal a new image as needed. If there is no active
     * image, the new image will snap in place. If there is already an active
     * image, the new image will slide in from the side.
     *
     * @private
     */
    dataChanged: function() {
        var activeImage = this.activeImage,
            direction   = this.get("direction"),
            data        = this.get("data"),
            shouldSnap  = !this.get("activeImage"),
            newImage;

        // Ensure an image for the current dataset
        newImage = data ? this.ensureImage(data) : null;

        // New image is already active, nothing to do
        if (newImage === activeImage) {
            return;
        }

        // New image, hide previous one and reveal new one as needed
        activeImage && this.hideImage(activeImage, direction === "forward" ? "left" : "right", false);
        newImage && this.revealImage(newImage, direction === "forward" ? "right" : "left", shouldSnap);
        this.set("activeImage", newImage);
    },

    /**
     * Retrieves a unique identifier from the dataset.
     *
     * @param   {*}        data  The data
     * @return  {?string}  The uid if found, `null` otherwise.
     * @abstract
     * @protected
     */
    getDataUid: function(data) {
        this.error("ott.image-viewer.Viewport must implement a `getDataUid` method.", data);

        return null;
    },

    /**
     * Determines if a previous image is available.
     *
     * @return {boolean} Whether a previous image is available.
     * @abstract
     * @protected
     */
    hasPreviousImage: function() {
        this.error("ott.image-viewer.Viewport must implement a `hasPreviousImage` method.");

        return false;
    },

    /**
     * Determines if a next image is available.
     *
     * @return {boolean} Whether a next image is available.
     * @abstract
     * @protected
     */
    hasNextImage: function() {
        this.error("ott.image-viewer.Viewport must implement a `hasNextImage` method.");

        return false;
    },

    // Scene Update
    // --------------------------------

    /**
     * @method
     * @private
     */
    update: kind.inherit(function(sup) {
        return function() {
            var imageKind = this.get("imageKind"),
                children  = this.children,
                child,
                i,
                n;

            // Search for expired images and remove them from the index
            for (i = 0, n = children.length; i < n; i++) {
                child = children[i];

                if (child.get("expired") && child instanceof imageKind) {
                    this.removeImageFromIndex(child);
                }
            }

            // Super method will handle destruction of the image
            sup.apply(this, arguments);
        };
    }),

    // Image Controls
    // --------------------------------

    /**
     * Checks for an existing image associated with the current data and
     * generates a new one if none exists.
     *
     * @param  {*}                           data  The data for the image
     *                                             control
     * @return {?enyo-canvas.CanvasControl}  The image if data is available,
     *                                       `null` otherwise.
     * @private
     */
    ensureImage: function(data) {
        var uid  = this.getDataUid(data),
            image;

        // No media or bad media type
        if (!uid) {
            return null;
        }

        // Check for existing image
        image = this.getImageForUid(uid);

        if (image) {
            return image;
        }

        // Generate image
        image = this.createImage(data);

        if (image) {
            return image;
        }

        return null;
    },

    /**
     * Gets the image associated with the provided uid.
     *
     * @param   {string}                      uid  The uid
     * @return  {?enyo-canvas.CanvasControl}  The image if found, `null`
     *                                        otherwise
     * @public
     */
    getImageForUid: function(uid) {
        return this.uidToImage[uid] || null;
    },

    /**
     * Create an image Control for the provided dataset.
     *
     * @param  {*}                          data  The data
     * @return {enyo-canvas.CanvasControl}  The created image
     * @private
     */
    createImage: function(data) {
        var direction  = this.get("direction"),
            uid        = this.getDataUid(data),
            uidToImage = this.uidToImage,
            image;

        // Image already exists
        if (uidToImage[uid]) {
            this.error("Image for uid: " + uid + " already exists.");

            return uidToImage[uid];
        }

        // Create image configured not to render immediately
        image = this.createComponent({
            name:     "viewport_image_" + uid,
            kind:     this.get("imageKind"),
            classes:  "ott-image-viewer-viewport__image",
            data:     data,
            viewport: this,
            showing:  false
        }, this.get("imageOptions") || {});

        // Hide image by default, it'll be revealed in due time
        this.hideImage(image, direction === "forward" ? "right" : "left", true);

        // Index image
        uidToImage[uid] = image;

        return image;
    },

    /**
     * Removes the image from the index.
     *
     * @param  {enyo-canvas.CanvasControl}  image  The image to remove
     * @private
     */
    removeImageFromIndex: function(image) {
        var data = image.get("data"),
            uid  = this.getDataUid(data);

        delete this.uidToImage[uid];
    },

    /**
     * Hide an image by sliding it out of view.
     *
     * @param  {enyo-canvas.CanvasControl}  image  The image to hide
     * @param  {string}                     side   The side to where to slide
     *                                             out
     * @param  {boolean}                    snap   Whether to snap the
     *                                             transition
     * @public
     */
    hideImage: function(image, side, snap) {
        var transform = image.transform;

        // Animate image out of view as needed
        transform.set("targetScaleX", "contain");
        transform.set("targetScaleY", "contain");
        transform.set("targetPositionX", side === "left" ? "hideLeft" : "hideRight");
        transform.set("targetPositionY", "center");
        snap && transform.finish();
    },

    /**
     * Reveal an image by sliding it into view.
     *
     * @param  {enyo-canvas.CanvasControl}  image  The image to reveal
     * @param  {string}                     side   The side from which to slide
     *                                             in
     * @param  {boolean}                    snap   Whether to snap the
     *                                             transition
     * @public
     */
    revealImage: function(image, side, snap) {
        var transform = image.transform;

        // If the image is not yet visible, place it just outside of view.
        if (!transform.isVisible()) {
            this.hideImage(image, side, true);
        }

        // Define centered target position for image.
        transform.set("targetScaleX", "contain");
        transform.set("targetScaleY", "contain");
        transform.set("targetPositionX", "center");
        transform.set("targetPositionY", "center");
        snap && transform.finish();
    },

    /**
     * Makes sure the previously active image is no longer marked as active.
     *
     * @param  {enyo-canvas.CanvasControl}  previousImage  The previously active
     *                                                    image
     * @private
     */
    activeImageChanged: function(previousImage) {
        var activeImage = this.activeImage;

        if (previousImage !== activeImage) {
            previousImage && previousImage.set("active", false);
            activeImage && activeImage.set("active", true);
        }
    }

});
